import React from 'react';
import './About.css';

const About: React.FC = () => {
  return (
    <div className="about-page">
      <div className="about-container">
        <div className="about-header">
          <h1 className="about-title">About GetStrandX</h1>
          <p className="about-subtitle">Roadside help and DIY car care — built by community, for community</p>
        </div>

        <div className="about-content">
          <section className="about-section">
            <h2 className="section-title">Our Mission</h2>
            <p className="section-text">
              At GetStrandX, we believe car care shouldn't be expensive or intimidating. Our mission is twofold:
              connect people who need roadside help with neighbors who can provide it, and teach everyday drivers
              how to handle their own maintenance — oil changes, tire care, fluid checks, and more.
            </p>
            <p className="section-text">
              We're building a comprehensive peer-to-peer platform that empowers communities to help each other,
              whether that's jumping your battery on the highway or walking you through your first oil change
              in the driveway. Real-time GPS matching, identity verification, step-by-step tutorials, and
              hands-on mentoring from experienced locals — all in one place.
            </p>
          </section>

          <section className="about-section">
            <h2 className="section-title">Our Story</h2>
            <p className="section-text">
              GetStrandX was born from two frustrations: traditional roadside assistance is expensive, slow,
              and unreliable — and most people have no idea how to do basic car maintenance themselves.
              We saw an opportunity to solve both problems with one community-powered platform.
            </p>
            <p className="section-text">
              Why pay a shop $80 for an oil change when a neighbor can teach you in 30 minutes?
              Why wait an hour for a tow truck when someone two blocks away can help right now?
              By connecting people who need help with those who can provide it — and teaching everyone
              the skills to be more self-sufficient — we're creating a smarter, more affordable car care ecosystem.
            </p>
          </section>

          <section className="about-section">
            <h2 className="section-title">What You Can Do on GetStrandX</h2>
            <ul className="about-list">
              <li><strong>Get Roadside Help:</strong> Flat tire, dead battery, lockout — connect with nearby helpers fast</li>
              <li><strong>Learn DIY Maintenance:</strong> Step-by-step guides for oil changes, tire rotations, fluid checks, and more</li>
              <li><strong>Find a Mentor:</strong> Connect with experienced locals for hands-on teaching sessions</li>
              <li><strong>Teach Others:</strong> Share your skills, earn money, and help your community become more self-sufficient</li>
              <li><strong>Smart Matching:</strong> Algorithm pairs you with the right helper or instructor based on location, skills, and ratings</li>
              <li><strong>Real-time Tracking:</strong> GPS location sharing and live updates during roadside assistance</li>
              <li><strong>Identity Verification:</strong> Government ID verification and optional background checks for safety</li>
              <li><strong>Flexible Payments:</strong> Pay for help with cards, PayPal, Venmo, or Zelle — or learn for free from generous neighbors</li>
            </ul>
          </section>

          <section className="about-section">
            <h2 className="section-title">Our Values</h2>
            <div className="values-grid">
              <div className="value-item">
                <h3 className="value-title">Community First</h3>
                <p className="value-description">
                  We believe in neighbors helping neighbors — whether that's fixing a flat or teaching an oil change.
                </p>
              </div>
              <div className="value-item">
                <h3 className="value-title">Empowerment</h3>
                <p className="value-description">
                  Learning to maintain your own car builds confidence, saves money, and makes you a better driver.
                </p>
              </div>
              <div className="value-item">
                <h3 className="value-title">Safety</h3>
                <p className="value-description">
                  Identity verification, safety checklists, and guided tutorials protect everyone — helpers and learners alike.
                </p>
              </div>
              <div className="value-item">
                <h3 className="value-title">Transparency</h3>
                <p className="value-description">
                  Clear pricing, honest communication, and open ratings build trust across the community.
                </p>
              </div>
            </div>
          </section>

          <section className="about-section">
            <h2 className="section-title">Join Our Mission</h2>
            <p className="section-text">
              We're currently in pre-launch and building our community. Whether you want roadside help,
              want to learn how to fix your own car, or have skills to teach — join our waitlist to be
              among the first to experience the future of community car care.
            </p>
            <div className="cta-section">
              <a href="/#prelaunch-survey" className="cta-button">
                Get Early Access
              </a>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default About;
